/**
 * PDM V2 factory match filtering (PDM only).
 *
 * Which body/rows substep drives card filtering is derived from PDM product
 * variation (same priority as card titles / filter slugs in the builder).
 */

import type {
  FactoryMatch,
  Step,
  StepConfiguration,
} from "@views/Upholstery/types"
import { isPdmStructureV2Enabled } from "../flags/isPdmStructureV2Enabled"

export type FactoryMatchFilterConfig = {
  step: string
  subStep: string
}

/** When PDM products do not vary on any tracked dimension, filter by trim. */
export const DEFAULT_FACTORY_MATCH_FILTER: FactoryMatchFilterConfig = {
  step: "body",
  subStep: "trim",
}

/** Extra keyPair on PDM factory match cards so trim can AND with the primary slug. */
export const FACTORY_MATCH_TRIM_KEYPAIR_KEY = "trim"

/**
 * Extra keyPair on PDM factory match cards so middle row can AND with the
 * primary slug + trim (Suburban LT: CHE2601G vs CHE2401E).
 */
export const FACTORY_MATCH_MIDDLE_KEYPAIR_KEY = "middle"

/** Filter tags — not shown in card headers / stored Factory Match labels. */
const FACTORY_MATCH_FILTER_KEYPAIR_KEYS = new Set([
  FACTORY_MATCH_TRIM_KEYPAIR_KEY,
  FACTORY_MATCH_MIDDLE_KEYPAIR_KEY,
])

/** True when this keyPair is a trim/middle filter tag (hide from display labels). */
export function isFactoryMatchFilterKeyPairKey(key: string): boolean {
  return FACTORY_MATCH_FILTER_KEYPAIR_KEYS.has(key.toLowerCase())
}

/**
 * Part-number (or first non-filter) key for UI labels. Falls back to `skuFallback`.
 */
export function factoryMatchDisplayLabelFromKeyPairs(
  keyPairs: { key: string }[] | null | undefined,
  skuFallback?: string | null
): string {
  return (
    keyPairs?.find((pair) => !isFactoryMatchFilterKeyPairKey(pair.key))?.key ||
    skuFallback?.trim() ||
    ""
  )
}

function selectedTrimLabel(activeConfigurations: StepConfiguration): string {
  return (
    activeConfigurations.body?.substeps?.trim?.value ??
    activeConfigurations.body?.substeps?.trim?.label ??
    ""
  ).trim()
}

function selectedMiddleLabel(activeConfigurations: StepConfiguration): string {
  return (
    activeConfigurations.rows?.substeps?.middle?.value ??
    activeConfigurations.rows?.substeps?.middle?.label ??
    ""
  ).trim()
}

/**
 * True if a comma-separated factory-match tag allows the selected value.
 * An empty tag means "show for every value" (untagged / legacy cards).
 */
function isFactoryMatchAllowedForTag(
  tag: string,
  selectedValue: string
): boolean {
  if (!selectedValue.trim()) return true
  const allowed = tag
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
  if (!allowed.length) return true
  return allowed.includes(selectedValue.trim().toLowerCase())
}

/**
 * True if a factory-match trim tag allows this selected trim.
 * Tags are comma-separated, same as body/row trim compatibility
 * (Silverado 2305X is Custom,LT,RST,WT — not only the first trim).
 * An empty tag means "show for every trim" (untagged / legacy cards).
 */
export function isFactoryMatchAllowedForTrim(
  trimTag: string,
  selectedTrim: string
): boolean {
  return isFactoryMatchAllowedForTag(trimTag, selectedTrim)
}

/**
 * True if a factory-match middle tag allows this selected middle row.
 * Tags are comma-separated (same as trim). Empty tag = show for every middle.
 */
export function isFactoryMatchAllowedForMiddle(
  middleTag: string,
  selectedMiddle: string
): boolean {
  return isFactoryMatchAllowedForTag(middleTag, selectedMiddle)
}

/**
 * Keep cards whose stored PDM trim list includes the selected trim.
 * Cards with no trim tag stay visible (other cars / legacy cards).
 * If nothing matches, keep the previous list so the step does not go blank.
 */
export function filterFactoryMatchesBySelectedTrim(
  factoryMatches: FactoryMatch[],
  selectedTrim: string
): FactoryMatch[] {
  if (!selectedTrim.trim() || !factoryMatches.length) return factoryMatches

  const next = factoryMatches.filter((item) => {
    const trimPair = item.keyPairs?.find(
      (kp) => kp.key.toLowerCase() === FACTORY_MATCH_TRIM_KEYPAIR_KEY
    )
    return isFactoryMatchAllowedForTrim(trimPair?.values ?? "", selectedTrim)
  })

  return next.length ? next : factoryMatches
}

function filterFactoryMatchesBySelectedTrimStrict(
  factoryMatches: FactoryMatch[],
  selectedTrim: string
): FactoryMatch[] {
  if (!selectedTrim.trim() || !factoryMatches.length) return factoryMatches
  return factoryMatches.filter((item) => {
    const trimPair = item.keyPairs?.find(
      (kp) => kp.key.toLowerCase() === FACTORY_MATCH_TRIM_KEYPAIR_KEY
    )
    return isFactoryMatchAllowedForTrim(trimPair?.values ?? "", selectedTrim)
  })
}

/**
 * Keep cards whose stored PDM middle-row list includes the selected middle.
 * Cards with no middle tag stay visible (cars without middle variation).
 * If nothing matches, keep the previous list so the step does not go blank.
 */
export function filterFactoryMatchesBySelectedMiddle(
  factoryMatches: FactoryMatch[],
  selectedMiddle: string
): FactoryMatch[] {
  if (!selectedMiddle.trim() || !factoryMatches.length) return factoryMatches

  const next = factoryMatches.filter((item) => {
    const middlePair = item.keyPairs?.find(
      (kp) => kp.key.toLowerCase() === FACTORY_MATCH_MIDDLE_KEYPAIR_KEY
    )
    return isFactoryMatchAllowedForMiddle(
      middlePair?.values ?? "",
      selectedMiddle
    )
  })

  return next.length ? next : factoryMatches
}

function filterFactoryMatchesBySelectedMiddleStrict(
  factoryMatches: FactoryMatch[],
  selectedMiddle: string
): FactoryMatch[] {
  if (!selectedMiddle.trim() || !factoryMatches.length) return factoryMatches
  return factoryMatches.filter((item) => {
    const middlePair = item.keyPairs?.find(
      (kp) => kp.key.toLowerCase() === FACTORY_MATCH_MIDDLE_KEYPAIR_KEY
    )
    return isFactoryMatchAllowedForMiddle(
      middlePair?.values ?? "",
      selectedMiddle
    )
  })
}

export type FactoryMatchVariation = {
  bodyTypesVary: boolean
  driverSeatsVary: boolean
  backRowsVary: boolean
  passengerSeatsVary: boolean
  trimsVary: boolean
  frontRowsVary: boolean
  middleRowsVary: boolean
  /**
   * True when each PDM Middle Row label maps to exactly one base kit.
   * When true, middle may filter cards even if trims also vary (Acadia).
   * When false, keep today's trim-before-middle order.
   */
  middleRowsUniquelyIdentifyKits: boolean
}

/**
 * Maps detected PDM variation to the body/rows substep whose selected slug
 * filters factory match cards. Priority matches `buildFactoryMatchDescriptiveName`.
 *
 * Body / driver / back / passenger stay first (unchanged).
 * Middle beats trim only when `middleRowsUniquelyIdentifyKits` is true.
 */
export function resolveFactoryMatchFilterConfig(
  variation: FactoryMatchVariation
): FactoryMatchFilterConfig {
  if (variation.bodyTypesVary) {
    return { step: "body", subStep: "body-step" }
  }
  if (variation.driverSeatsVary) {
    return { step: "body", subStep: "driver-seat-type" }
  }
  if (variation.backRowsVary) {
    return { step: "rows", subStep: "back" }
  }
  if (variation.passengerSeatsVary) {
    return { step: "body", subStep: "passenger-seat-type" }
  }
  if (variation.middleRowsVary && variation.middleRowsUniquelyIdentifyKits) {
    return { step: "rows", subStep: "middle" }
  }
  if (variation.trimsVary) {
    return { step: "body", subStep: "trim" }
  }
  if (variation.frontRowsVary) {
    return { step: "rows", subStep: "front" }
  }
  if (variation.middleRowsVary) {
    return { step: "rows", subStep: "middle" }
  }
  return DEFAULT_FACTORY_MATCH_FILTER
}

export function getFactoryMatchFilterFromSteps(
  steps: Step[] | undefined
): FactoryMatchFilterConfig | undefined {
  return steps?.find((s) => s.key === "factory-match")?.factoryMatchFilter
}

/**
 * Filter config for the active vehicle.
 *
 * - `NEXT_PUBLIC_UPHOLSTERY_PDM_V2_STRUCTURE=true`: PDM `factoryMatchFilter` on the
 *   factory-match step only (never Dato `vehicle.options.factoryMatch`).
 * - Flag off: same as pre-fix store — PDM filter on step when present, else Dato
 *   `vehicle.options.factoryMatch`.
 */
export function getEffectiveFactoryMatchFilterConfig(
  steps: Step[] | undefined,
  vehicleOptions: unknown
): FactoryMatchFilterConfig | undefined {
  if (isPdmStructureV2Enabled()) {
    return getFactoryMatchFilterFromSteps(steps)
  }

  const pdmFilter = getFactoryMatchFilterFromSteps(steps)
  if (pdmFilter) {
    return pdmFilter
  }

  return parseLegacyFactoryMatchFilterFromDatoOptions(vehicleOptions)
}

function shouldUsePdmFactoryMatchFilter(steps: Step[] | undefined): boolean {
  if (isPdmStructureV2Enabled()) {
    return true
  }
  return !!getFactoryMatchFilterFromSteps(steps)
}

export function isFactoryMatchFilterSubstep(
  filterConfig: FactoryMatchFilterConfig | undefined,
  stepKey: string,
  subStepKey: string | null
): boolean {
  if (!filterConfig || !subStepKey) return false
  return filterConfig.step === stepKey && filterConfig.subStep === subStepKey
}

/**
 * Shared factory match card filter for UI and SKU resolution.
 *
 * Matches pre-fix `getFilteredFactoryMatches` behavior:
 * - V2 flag on, or `factoryMatchFilter` on step → `filterFactoryMatchesForPdm`
 * - Otherwise → `filterFactoryMatchesLegacy` (Dato `options.factoryMatch`)
 */
export function filterFactoryMatchesForActiveConfig(
  factoryMatches: FactoryMatch[],
  activeConfigurations: StepConfiguration,
  steps: Step[] | undefined,
  vehicleOptions: unknown
): FactoryMatch[] {
  if (shouldUsePdmFactoryMatchFilter(steps)) {
    const filterConfig = getFactoryMatchFilterFromSteps(steps)
    if (!filterConfig) {
      return factoryMatches
    }
    return filterFactoryMatchesForPdm(
      factoryMatches,
      activeConfigurations,
      filterConfig
    )
  }

  return filterFactoryMatchesLegacy(
    factoryMatches,
    activeConfigurations,
    vehicleOptions
  )
}

/**
 * Legacy upholstery only (non-V2). Reads Dato `vehicle.options.factoryMatch`.
 * UpholsteryV2 never uses this — it sets `step.factoryMatchFilter` from PDM instead.
 */
export function parseLegacyFactoryMatchFilterFromDatoOptions(
  vehicleOptions: unknown
): FactoryMatchFilterConfig | undefined {
  if (!vehicleOptions || typeof vehicleOptions !== "object") {
    return undefined
  }

  const factoryMatch = (
    vehicleOptions as { factoryMatch?: { step?: string; subStep?: string } }
  ).factoryMatch

  const step = factoryMatch?.step?.trim()
  const subStep = factoryMatch?.subStep?.trim()

  if (!step || !subStep) {
    return undefined
  }

  return { step, subStep }
}

/**
 * Legacy upholstery factory match filtering (original store behavior).
 */
export function filterFactoryMatchesLegacy(
  factoryMatches: FactoryMatch[],
  activeConfigurations: StepConfiguration,
  vehicleOptions: unknown
): FactoryMatch[] {
  const filterConfig =
    parseLegacyFactoryMatchFilterFromDatoOptions(vehicleOptions)

  if (!filterConfig) {
    return factoryMatches
  }

  const selectedValue =
    activeConfigurations[filterConfig.step]?.substeps?.[filterConfig.subStep]

  if (!selectedValue) {
    return factoryMatches
  }

  const slug = selectedValue.slug?.trim()
  if (!slug) {
    return factoryMatches
  }

  return factoryMatches.filter((item) =>
    item.keyPairs?.some((keyPair) => keyPair.values === slug)
  )
}

/**
 * Apply trim then middle extras after the primary slug filter.
 * Both are fail-soft: empty results keep the previous list.
 */
function applyTrimAndMiddleExtras(
  cards: FactoryMatch[],
  activeConfigurations: StepConfiguration
): FactoryMatch[] {
  const afterTrim = filterFactoryMatchesBySelectedTrim(
    cards,
    selectedTrimLabel(activeConfigurations)
  )
  return filterFactoryMatchesBySelectedMiddle(
    afterTrim,
    selectedMiddleLabel(activeConfigurations)
  )
}

/**
 * Show only PDM factory match cards whose `keyPairs[].values` equals the slug
 * from the configured body/rows substep selection.
 * Then AND by selected trim and middle (fail-soft).
 *
 * Middle has a trim-style fallback: when the primary slug isolates a card that
 * does not match the selected middle (Suburban LT + driver-seat primary filter),
 * re-apply middle against all trim-matching cards so the correct kit can surface.
 */
export function filterFactoryMatchesForPdm(
  factoryMatches: FactoryMatch[],
  activeConfigurations: StepConfiguration,
  filterConfig: FactoryMatchFilterConfig
): FactoryMatch[] {
  const applyExtras = (cards: FactoryMatch[]) =>
    applyTrimAndMiddleExtras(cards, activeConfigurations)

  const selectedValue =
    activeConfigurations[filterConfig.step]?.substeps?.[filterConfig.subStep]

  if (!selectedValue) {
    return applyExtras(factoryMatches)
  }

  const slug = selectedValue.slug?.trim()
  if (!slug) {
    return applyExtras(factoryMatches)
  }

  const filtered = factoryMatches.filter((item) =>
    item.keyPairs?.some((keyPair) => keyPair.values === slug)
  )

  const selectedTrim = selectedTrimLabel(activeConfigurations)
  const selectedMiddle = selectedMiddleLabel(activeConfigurations)
  const usedFallback = filtered.length === 0 && factoryMatches.length > 0
  const slugOrAll = usedFallback ? factoryMatches : filtered
  const strictTrimSlug = filterFactoryMatchesBySelectedTrimStrict(
    slugOrAll,
    selectedTrim
  )
  const strictTrimAll = filterFactoryMatchesBySelectedTrimStrict(
    factoryMatches,
    selectedTrim
  )
  const afterTrim = strictTrimSlug.length
    ? strictTrimSlug
    : strictTrimAll.length
    ? strictTrimAll
    : filterFactoryMatchesBySelectedTrim(factoryMatches, selectedTrim)

  const middleOnSlugTrim = filterFactoryMatchesBySelectedMiddleStrict(
    afterTrim,
    selectedMiddle
  )
  const trimUniverse = strictTrimAll.length
    ? strictTrimAll
    : filterFactoryMatchesBySelectedTrim(factoryMatches, selectedTrim)
  const middleOnTrimAll = filterFactoryMatchesBySelectedMiddleStrict(
    trimUniverse,
    selectedMiddle
  )

  const result = middleOnSlugTrim.length
    ? middleOnSlugTrim
    : middleOnTrimAll.length
    ? middleOnTrimAll
    : afterTrim

  // Safety: avoid blank factory-match step when slug/card mapping drifts (e.g. wrong substep).
  if (usedFallback) {
    console.warn(
      "🏭 [Factory Match] Slug filter matched no cards; showing all PDM cards.",
      { filterConfig, slug, cardCount: factoryMatches.length }
    )
  }

  return result
}
